import React, { useState, useEffect } from 'react';
import { X, Gift } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { appStorage } from '@/lib/appStorage';
import RevenueCatService from '@/services/revenueCat';
import type { CustomerInfo } from '@revenuecat/purchases-capacitor';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const BANNER_STORAGE_KEY = 'promo_banner_dismissed';

const PromoBanner: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [shouldShow, setShouldShow] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkBannerVisibility = async () => {
      try {
        // 1. Verificar se usuário está logado
        if (!user) {
          setIsLoading(false);
          return;
        }

        // 2. Verificar se banner já foi dismisso
        const bannerDismissed = await appStorage.getItem(BANNER_STORAGE_KEY);
        if (bannerDismissed === 'true') {
          setIsLoading(false);
          return;
        }

        // 3. Verificar se usuário é assinante
        try {
          const customerInfo = await RevenueCatService.getCustomerInfo();
          const isSubscribed = (customerInfo.customerInfo as CustomerInfo).entitlements.active['premium'] !== undefined;
          
          if (isSubscribed) {
            setIsLoading(false);
            return;
          }
        } catch (error) {
          // Se não conseguir verificar o RevenueCat, usa o tipo de conta do usuário
          if (user.tipo_de_conta === 'premium') {
            setIsLoading(false);
            return;
          }
        }

        // 4. Se chegou até aqui, pode mostrar o banner
        setShouldShow(true);
      } catch (error) {
        console.error('Erro ao verificar visibilidade do banner:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkBannerVisibility();
  }, [user]);

  const handleDismiss = async () => {
    try {
      await appStorage.setItem(BANNER_STORAGE_KEY, 'true');
      setShouldShow(false);
    } catch (error) {
      console.error('Erro ao dismissar banner:', error);
      setShouldShow(false);
    }
  };

  const handleStartTrial = async () => {
    try {
      await appStorage.setItem(BANNER_STORAGE_KEY, 'true');
      setShouldShow(false);
      // Navegar para settings onde está a lógica de assinatura
      navigate('/settings', { 
        state: { 
          focusOnSubscription: true,
          preferMonthly: true 
        } 
      });
    } catch (error) {
      console.error('Erro ao iniciar teste:', error);
      navigate('/settings');
    }
  };

  // Não renderizar se estiver carregando ou não deve mostrar
  if (isLoading || !shouldShow) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <Card className="relative w-full max-w-sm mx-auto bg-white shadow-2xl border-0 rounded-3xl overflow-hidden">
        {/* Header com gradiente */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 text-center relative">
          {/* Botão de fechar */}
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors backdrop-blur-sm"
            aria-label="Fechar banner"
          >
            <X className="w-4 h-4 text-white" />
          </button>

          <div className="flex justify-center mb-3">
            <div className="bg-white/20 p-3 rounded-full backdrop-blur-sm">
              <Gift className="w-8 h-8 text-white" />
            </div>
          </div>
          
          {/* PREÇO COMO ELEMENTO PRINCIPAL - Guideline 3.1.2 */}
          <div className="mb-3">
            <h2 className="text-3xl font-bold text-white mb-1">
              R$ 9,90
            </h2>
            <p className="text-lg text-white/90 font-semibold">
              por mês
            </p>
          </div>
          
          {/* INFORMAÇÃO DO TRIAL EM TAMANHO MENOR */}
          <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-3">
            <span className="text-white/90 font-medium text-sm">
              7 dias grátis, depois R$ 9,90/mês
            </span>
          </div>
          
          <h3 className="text-lg font-bold text-white mb-2">
            Experimente Premium
          </h3>
          <p className="text-white/90 text-sm leading-relaxed">
            Ouça louvores com trechos reais da Bíblia, sem interrupções.
          </p>
        </div>

        {/* Conteúdo */}
        <div className="p-6">
          <div className="space-y-3 mb-6">
            <div className="flex items-center gap-3 text-gray-700">
              <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
              <span className="text-sm">Todas as músicas liberadas</span>
            </div>
            <div className="flex items-center gap-3 text-gray-700">
              <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
              <span className="text-sm">Jornada Bíblica completa</span>
            </div>
            <div className="flex items-center gap-3 text-gray-700">
              <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
              <span className="text-sm">Músicas offline</span>
            </div>
          </div>
          
          <Button
            onClick={handleStartTrial}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold py-3 px-6 rounded-2xl transition-all shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
          >
            Começar meu teste gratuito
          </Button>
          
          <p className="text-xs text-gray-500 text-center mt-3">
            Cancele a qualquer momento
          </p>
        </div>
      </Card>
    </div>
  );
};

export default PromoBanner; 